import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";

import { getLeadboxNavSections, isLeadboxNavLinkActive } from "./leadboxNavModel";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";

interface LeadboxSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

export function LeadboxSidebar({ isCollapsed, setIsCollapsed }: LeadboxSidebarProps) {
  const location = useLocation();
  const { canViewBidsOverview } = useCurrentAdminPermissions();
  const { primary, tools, footer } = getLeadboxNavSections({
    canViewBidsOverview,
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed, setIsCollapsed]);

  const rowLinkClass = (url: string) => {
    const active = isLeadboxNavLinkActive(url, location.pathname);
    return `flex items-center gap-3 transition-colors ${
      active
        ? "text-gray-900 dark:text-white font-medium"
        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
    } ${isCollapsed ? "justify-center md:justify-start" : ""}`;
  };

  const renderLink = (item: { title: string; url: string; icon: LucideIcon }) => (
    <Link
      key={item.url}
      to={item.url}
      className={rowLinkClass(item.url)}
      title={isCollapsed ? item.title : ""}
    >
      <item.icon className="h-5 w-5 min-w-[20px]" />
      <span className={`${isCollapsed ? "hidden" : ""} text-sm whitespace-nowrap`}>
        {item.title}
      </span>
    </Link>
  );

  return (
    <aside
      className={`relative shrink-0 self-stretch min-h-[calc(100dvh-4rem)] p-3 flex flex-col border-r border-border bg-background shadow-sm transition-all duration-300 ease-in-out min-h-0 ${
        isCollapsed ? "w-14" : "w-48"
      }`}
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-6 bg-background dark:bg-background rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-800 md:flex hidden z-10 border border-border"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </Button>

      <div className="flex flex-col pt-2 md:pt-14 gap-1 overflow-y-auto min-h-0">
        <nav className="flex-1 space-y-6">
          <div className="space-y-1">{primary.map(renderLink)}</div>
          <div className="h-px bg-border mx-2" />
          <div className="space-y-1">{tools.map(renderLink)}</div>
        </nav>
        <div className="space-y-4 mt-auto pt-4 border-t border-border">
          {footer.map(renderLink)}
        </div>
      </div>
    </aside>
  );
}
